/*
    useStep2Logic.ts -> step 2: 문제 맞추기 단계
*/

import { useEffect, useRef, useState } from "react";
import { Obj } from "../../types/objectTypes";
import { isInDropZone } from "../../utils/solveProblem";
import { HandleHandActions } from "../../utils/HandleHandActions";
// import { drawDropZone } from "../../utils/draw";
import { useNavigate } from "react-router-dom";

// 선택지 카드 크기
const card_width = 120;
const card_height = 170;

// 버튼 크기
const button_width = 100;
const button_height = 100;

// 드롭존 좌표
const dx = 800; // 왼쪽 위 x좌표
const dy = 450; // 왼쪽 위 y좌표
const dw = 500; // 가로 길이
const dh = 400; // 세로 길이

export const useStep2Logic = ({
  probImage,
  answer,
  wrongList,
  canvasRef,
  camRatioRef,
  onOpenModal,
}: any) => {
  const navigate = useNavigate();
  const selectAnswerRef = useRef<number | null>(null);
  const [selectAnswer, setSelectAnswer] = useState<number | null>(null);

  // 정답
  let choiceAnswer = isNaN(Number(answer)) ? 1 : Number(answer);

  const [objects, setObjects] = useState<Obj[]>([]);
  useEffect(() => {
    if (!probImage) return;

    // 선택지 추가
    let count = 1;
    const choiceOptions: number[] = [];
    choiceOptions.push(choiceAnswer);
    for (const wrong of wrongList) {
      if (!wrong) continue;
      count += 1;
      choiceOptions.push(isNaN(Number(wrong)) ? count : Number(wrong));
    }

    // 선택지 이미지 추가
    const choiceImages: string[] = [];
    choiceImages.push(answer);
    for (const wrong of wrongList) {
      if (!wrong) continue;
      choiceImages.push(wrong);
    }

    setObjects(getStep2ObjectsInfo(probImage, choiceOptions, choiceImages));
  }, [answer, choiceAnswer, probImage, wrongList]);

  const objectsRef = useRef(objects);

  /* 템플릿 로직 */
  // object 변경되면 업데이트
  useEffect(() => {
    objectsRef.current = objects;
  }, [objects]);

  // selectAnswer 변경되면 업데이트
  useEffect(() => {
    selectAnswerRef.current = selectAnswer;
  }, [selectAnswer, selectAnswerRef]);

  // 고른 정답이 변경되면 업데이트
  useEffect(() => {
    selectAnswerRef.current = selectAnswer;
    // 고른 정답의 정답 여부에 따라 이미지 업데이트
    setObjects((prev) =>
      prev.map((obj) => {
        // 말풍선 업데이트
        if (obj.id === "mention") {
          let mention_image = "/asset/check-answer/init-mention.png";
          if (selectAnswer) {
            if (selectAnswer === choiceAnswer)
              mention_image = "/asset/check-answer/correct-mention.png";
            else mention_image = "/asset/check-answer/incorrect-mention.png";
          }
          return {
            ...obj,
            src: mention_image,
          };
        }

        // 곰 이미지 업데이트
        if (obj.id === "bear") {
          let bear_image = "/asset/check-answer/init-bear.png";
          if (selectAnswer) {
            if (selectAnswer === choiceAnswer)
              bear_image = "/asset/check-answer/correct-bear.png";
            else bear_image = "/asset/check-answer/incorrect-bear.png";
          }
          return {
            ...obj,
            src: bear_image,
          };
        }

        // 아무 조건에도 해당되지 않으면 그대로 유지
        return obj;
      })
    );
  }, [answer, choiceAnswer, selectAnswer, selectAnswerRef]);

  /* Mediapipe 관련 로직 */
  function onResults(results: any) {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dispW = canvas.clientWidth;
    const dispH = canvas.clientHeight;
    const ratio = camRatioRef.current;

    ctx.save();
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // 드롭존 안에 선택지가 있는지 확인
    setSelectAnswer(null);
    objectsRef.current.forEach(({ x, y, isObj, value }) => {
      const ox = x;
      const oy = y;
      if (isInDropZone(ratio, dx, dy, dw, dh, ox, oy, isObj)) {
        // 객체가 드롭존 안에 있다면
        setSelectAnswer(value);
      }
    });

    HandleHandActions(
      results,
      ctx,
      ratio,
      dispW,
      dispH,
      setObjects,
      navigate,
      objectsRef,
      onOpenModal
    );
  }

  return { objects, onResults };
};

/* 1600 x 900을 기준으로 배치 */
export function getStep2ObjectsInfo(
  prob_image: string,
  choiceOptions: number[],
  choiceImages: string[]
): Obj[] {
  const answerInfo: Obj[] = [
    // 정답 맞추기 위한 객체
    // 처음엔 아무것도 없음
  ];

  // 되돌아가기 버튼
  answerInfo.push({
    id: "button-back-from-check",
    x: 100,
    y: 100,
    src: "/asset/button/button-back.png",
    isObj: false, // 객체 아님
    value: 1, // 버튼
    width: button_width,
    height: button_height,
  });

  // 다른 문제 풀러가기 버튼
  answerInfo.push({
    id: "button-home",
    x: 1500,
    y: 100,
    src: "/asset/button/button-home.png",
    isObj: false, // 객체 아님
    value: 1, // 버튼
    width: button_width,
    height: button_height,
  });

  // 알아보기 버튼
  answerInfo.push({
    id: "button-commentary-info",
    x: 100,
    y: 800,
    src: `/asset/button/button-gesture-info.png`,
    isObj: false, // 객체 아님
    value: 1, // 버튼
    width: button_width,
    height: button_height,
  });

  // 문제 놓을 박스
  answerInfo.push({
    id: "prob-box",
    x: 800,
    y: 250,
    src: "/asset/check-answer/prob-box.png",
    isObj: false, // 객체 아님
    value: null,
    width: 600,
    height: 300,
  });

  // 문제 사진
  answerInfo.push({
    id: "prob-image",
    x: 800,
    y: 250,
    src: prob_image,
    isObj: false, // 객체 아님
    value: null,
    width: 580,
    height: 280,
  });

  // 선택지 놓을 드롭박스
  answerInfo.push({
    id: "select-box",
    x: 1050,
    y: 650,
    src: "/asset/check-answer/select-box.png",
    isObj: false, // 객체 아님
    value: null,
    width: 400,
    height: 300,
  });

  // 말풍선
  answerInfo.push({
    id: "mention",
    x: 1450,
    y: 350,
    src: "/asset/check-answer/init-mention.png",
    isObj: false, // 객체 아님
    value: null,
    width: 200,
    height: 150,
  });

  // 캐릭터
  answerInfo.push({
    id: "bear",
    x: 1450,
    y: 650,
    src: "/asset/check-answer/init-bear.png",
    isObj: false, // 객체 아님
    value: null,
    width: 200,
    height: 300,
  });

  const choices: number[] = choiceOptions;
  choices.sort(); // 오름차순으로 정렬
  choiceImages.sort(); // 오름차순으로 정렬

  const start_x = 200;
  let gap = 200;
  if (choices.length < 3) gap = 400;

  // 선택지 1
  answerInfo.push({
    id: "choice1",
    x: start_x,
    y: 650,
    src: `/asset/check-answer/card/${choiceImages[0]}.png`,
    isObj: true, // 객체임
    value: choices[0],
    width: card_width,
    height: card_height,
  });

  // 선택지 2
  answerInfo.push({
    id: "choice2",
    x: start_x + gap,
    y: 650,
    src: `/asset/check-answer/card/${choiceImages[1]}.png`,
    isObj: true, // 객체임
    value: choices[1],
    width: card_width,
    height: card_height,
  });

  // 선택지 3
  if (choices.length >= 3) {
    answerInfo.push({
      id: "choice3",
      x: start_x + gap + gap,
      y: 650,
      src: `/asset/check-answer/card/${choiceImages[2]}.png`,
      isObj: true, // 객체임
      value: choices[2],
      width: card_width,
      height: card_height,
    });
  }

  return answerInfo;
}
